import React, { useState, useEffect } from 'react';
import { getApiEndpoint } from '../../../../config/api';
import './Handrails.css';
import { toast } from 'react-toastify';
import AssetDetailModal from '../shared/AssetDetailModal';
import { openLatestTestReport } from '../shared/openLatestTestReport';
import { isSuperAdmin } from '../../../../utils/roleUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faTrash, faSearch, faFilter, faShieldAlt, faPlus, faXmark, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';

const HANDRAIL_TYPES = [
  'Standard',
  'Custom',
  'Temporary',
  'Permanent'
];

const HANDRAIL_MATERIALS = [
  'Stainless Steel',
  'Galvanized Steel',
  'Aluminum',
  'Other'
];

const getBadgeClass = (status) => {
  const key = (status || 'unknown').toLowerCase();
  const map = {
    pass: 'rs-badge-pass',
    passed: 'rs-badge-passed',
    fail: 'rs-badge-fail',
    failed: 'rs-badge-failed',
    pending: 'rs-badge-pending',
    overdue: 'rs-badge-overdue',
    unknown: 'rs-badge-unknown'
  };
  return `rs-badge ${map[key] || 'rs-badge-unknown'}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB');
  } catch {
    return 'N/A';
  }
};

const INITIAL_HANDRAIL = {
  location: '',
  type: HANDRAIL_TYPES[0],
  length: '',
  height: '',
  material: HANDRAIL_MATERIALS[0],
  installedBy: '',
  dateInstalled: new Date().toISOString().split('T')[0],
  lastTestDate: new Date().toISOString().split('T')[0],
  certificateUrl: '',
  notes: '',
  status: 'Unknown'
};

export default function Handrails({ propertyId }) {
  const [handrails, setHandrails] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingHandrail, setEditingHandrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newHandrail, setNewHandrail] = useState({ ...INITIAL_HANDRAIL });
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailAsset, setDetailAsset] = useState(null);

  const fetchHandrailsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/handrails`), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`Failed to fetch handrails: ${response.status}`);

      const data = await response.json();
      const formattedData = (Array.isArray(data) ? data : [data]).map(handrail => ({
        id: handrail.id,
        propertyId: handrail.property_id,
        name: handrail.handrail_name || handrail.name || `Handrail ${handrail.id}`,
        location: handrail.location || handrail.location_description,
        type: handrail.type || 'Fixed',
        length: handrail.length ? `${handrail.length}m` : 'N/A',
        height: handrail.height ? `${handrail.height}m` : 'N/A',
        material: handrail.material || 'N/A',
        installedBy: handrail.installed_by || '',
        dateInstalled: handrail.date_installed ? new Date(handrail.date_installed).toISOString().split('T')[0] : '',
        lastTestDate: handrail.last_inspection_date ? new Date(handrail.last_inspection_date).toISOString().split('T')[0] : '',
        nextTestDate: handrail.next_inspection_date
          ? new Date(handrail.next_inspection_date).toISOString().split('T')[0]
          : (handrail.last_inspection_date
            ? new Date(new Date(handrail.last_inspection_date).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
            : ''),
        certificateUrl: handrail.certificate_url,
        notes: handrail.notes || '',
        status: handrail.status || 'Unknown'
      }));

      setHandrails(formattedData);
    } catch (err) {
      console.error('Error in fetchHandrailsData:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to fetch handrails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId) fetchHandrailsData();
  }, [propertyId]);

  // Auto-open asset from drawing hotspot navigation
  useEffect(() => {
    const handler = (e) => {
      const { assetId, assetTable } = e.detail || {};
      if (assetTable === 'handrails' && assetId && handrails.length) {
        const asset = handrails.find(a => a.id === parseInt(assetId));
        if (asset) setDetailAsset(asset);
      }
    };
    window.addEventListener('openAssetFromHotspot', handler);
    return () => window.removeEventListener('openAssetFromHotspot', handler);
  }, [handrails]);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('.rs-filter-dropdown') && !e.target.closest('.filter-toggle-btn')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  const handleCreateHandrail = async () => {
    try {
      const token = localStorage.getItem('token');
      const requestData = {
        location: newHandrail.location,
        type: newHandrail.type,
        length: newHandrail.length,
        height: newHandrail.height,
        material: newHandrail.material,
        installed_by: newHandrail.installedBy,
        date_installed: newHandrail.dateInstalled,
        last_inspection_date: newHandrail.lastTestDate,
        certificate_url: newHandrail.certificateUrl,
        notes: newHandrail.notes
      };

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/handrails`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) throw new Error('Failed to create handrail');

      const createdHandrail = await response.json();
      setHandrails([...handrails, createdHandrail]);
      setShowCreateModal(false);
      setNewHandrail({ ...INITIAL_HANDRAIL });
      toast.success('Handrail created successfully');
    } catch (error) {
      console.error('Error creating handrail:', error);
      toast.error('Failed to create handrail');
    }
  };

  const handleEditHandrail = async () => {
    try {
      const token = localStorage.getItem('token');
      const requestData = {
        handrail_name: editingHandrail.name || editingHandrail.location,
        location_description: editingHandrail.location,
        type: editingHandrail.type,
        length: editingHandrail.length,
        height: editingHandrail.height,
        material: editingHandrail.material,
        installed_by: editingHandrail.installedBy,
        date_installed: editingHandrail.dateInstalled,
        last_inspection_date: editingHandrail.lastTestDate || null,
        certificate_url: editingHandrail.certificateUrl || null,
        notes: editingHandrail.notes || '',
        status: editingHandrail.status || 'Unknown'
      };
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/handrails/${editingHandrail.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) throw new Error('Failed to update handrail');

      const updatedHandrail = await response.json();
      setHandrails(handrails.map(h => h.id === updatedHandrail.id ? updatedHandrail : h));
      setShowEditModal(false);
      setEditingHandrail(null);
      toast.success('Handrail updated successfully');
    } catch (error) {
      console.error('Error updating handrail:', error);
      toast.error('Failed to update handrail');
    }
  };

  const handleDeleteHandrail = async (id) => {
    if (!window.confirm('Are you sure you want to delete this handrail?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/handrails/${id}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete handrail');
      setHandrails(handrails.filter(h => h.id !== id));
      toast.success('Handrail deleted successfully');
    } catch (error) {
      console.error('Error deleting handrail:', error);
      toast.error('Failed to delete handrail');
    }
  };

  const handleViewTestData = (handrail) => {
    openLatestTestReport({
      propertyId,
      assetType: 'handrail',
      assetId: handrail.id,
      fallbackAsset: handrail,
      emptyMessage: 'No test data available for this handrail',
      onEmpty: (msg) => toast.info(msg),
    });
  };

  // Filtered data
  const filtered = handrails
    .filter(h => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return [h.name, h.location, h.type, h.material, h.status].some(v => v && v.toLowerCase().includes(q));
    })
    .filter(h => typeFilter === 'All' || h.type === typeFilter);

  // ─── Error State ───
  if (error) {
    return (
      <div className="handrails-section">
        <h1>Handrails</h1>
        <div className="rs-error-alert">
          <FontAwesomeIcon icon={faShieldAlt} />
          <span>{error}</span>
          <button onClick={fetchHandrailsData}>Retry</button>
        </div>
      </div>
    );
  }

  // ─── Loading State ───
  if (loading) {
    return (
      <div className="handrails-section">
        <h1>Handrails</h1>
        <div className="rs-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="rs-skeleton-row" />)}
        </div>
      </div>
    );
  }

  // ─── Form Fields Renderer ───
  const renderFormFields = (data, setData) => (
    <div className="rs-form-grid">
      <div className="rs-form-group">
        <label>Location</label>
        <input
          type="text"
          value={data.location || ''}
          onChange={(e) => setData({ ...data, location: e.target.value })}
          required
        />
      </div>
      <div className="rs-form-group">
        <label>Type</label>
        <select
          value={data.type || ''}
          onChange={(e) => setData({ ...data, type: e.target.value })}
          required
        >
          {HANDRAIL_TYPES.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>
      <div className="rs-form-group">
        <label>Length</label>
        <input
          type="text"
          value={data.length || ''}
          onChange={(e) => setData({ ...data, length: e.target.value })}
          required
        />
      </div>
      <div className="rs-form-group">
        <label>Height</label>
        <input
          type="text"
          value={data.height || ''}
          onChange={(e) => setData({ ...data, height: e.target.value })}
          required
        />
      </div>
      <div className="rs-form-group">
        <label>Material</label>
        <select
          value={data.material || ''}
          onChange={(e) => setData({ ...data, material: e.target.value })}
          required
        >
          {HANDRAIL_MATERIALS.map(material => (
            <option key={material} value={material}>{material}</option>
          ))}
        </select>
      </div>
      <div className="rs-form-group">
        <label>Installed By</label>
        <input
          type="text"
          value={data.installedBy || ''}
          onChange={(e) => setData({ ...data, installedBy: e.target.value })}
          required
        />
      </div>
      <div className="rs-form-group">
        <label>Date Installed</label>
        <input
          type="date"
          value={data.dateInstalled || ''}
          onChange={(e) => setData({ ...data, dateInstalled: e.target.value })}
          required
        />
      </div>
      <div className="rs-form-group">
        <label>Last Test Date</label>
        <input
          type="date"
          value={data.lastTestDate || ''}
          onChange={(e) => setData({ ...data, lastTestDate: e.target.value })}
          required
        />
      </div>
      <div className="rs-form-group">
        <label>Certificate URL</label>
        <input
          type="text"
          value={data.certificateUrl || ''}
          onChange={(e) => setData({ ...data, certificateUrl: e.target.value })}
        />
      </div>
      <div className="rs-form-group rs-full-width">
        <label>Notes</label>
        <textarea
          value={data.notes || ''}
          onChange={(e) => setData({ ...data, notes: e.target.value })}
        />
      </div>
    </div>
  );

  return (
    <div className="handrails-section">
      <h1>Handrails</h1>

      {/* Toolbar */}
      <div className="rs-toolbar">
        <div className="rs-search-bar">
          <FontAwesomeIcon icon={faSearch} className="rs-search-icon" />
          <input
            type="text"
            placeholder="Search handrails..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table / Cards */}
      <div className="rs-table-card">
        {/* Desktop Table */}
        <div className="rs-table-scroll">
          <table className="rs-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Location</th>
                <th>Type</th>
                <th>Length</th>
                <th>Height</th>
                <th>Material</th>
                <th>Installed By</th>
                <th>Date Installed</th>
                <th>Last Test</th>
                <th>Next Test</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="13">
                    <div className="rs-empty-state">
                      <div className="rs-empty-icon"><FontAwesomeIcon icon={faShieldAlt} /></div>
                      <h3 className="rs-empty-title">
                        {handrails.length === 0 ? 'No handrails yet' : 'No results found'}
                      </h3>
                      <p className="rs-empty-desc">
                        {handrails.length === 0
                          ? 'Add your first handrail to start tracking safety compliance.'
                          : 'Try adjusting your search or filter criteria.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map(handrail => (
                <tr key={handrail.id}>
                  <td title={handrail.id}>{handrail.id}</td>
                  <td title={handrail.name}>{handrail.name}</td>
                  <td title={handrail.location}>{handrail.location}</td>
                  <td>{handrail.type}</td>
                  <td>{handrail.length}</td>
                  <td>{handrail.height}</td>
                  <td>{handrail.material}</td>
                  <td>{handrail.installedBy || 'N/A'}</td>
                  <td>{formatDate(handrail.dateInstalled)}</td>
                  <td>{formatDate(handrail.lastTestDate)}</td>
                  <td>{formatDate(handrail.nextTestDate)}</td>
                  <td>
                    <span className={getBadgeClass(handrail.status)}>
                      {handrail.status || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <div className="rs-actions">
                      <button className="rs-action-btn" onClick={() => handleViewTestData(handrail)} title="View Test Data" aria-label="View test data">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="rs-action-btn" onClick={() => setDetailAsset(handrail)} title="Test History" aria-label="View test history">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                      {isSuperAdmin() && (<>
                      <button className="rs-action-btn" onClick={() => { setEditingHandrail(handrail); setShowEditModal(true); }} title="Edit" aria-label="Edit handrail">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="rs-action-btn rs-action-delete" onClick={() => handleDeleteHandrail(handrail.id)} title="Delete" aria-label="Delete handrail">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="rs-mobile-cards">
          {filtered.length === 0 ? (
            <div className="rs-empty-state">
              <div className="rs-empty-icon"><FontAwesomeIcon icon={faShieldAlt} /></div>
              <h3 className="rs-empty-title">
                {handrails.length === 0 ? 'No handrails yet' : 'No results found'}
              </h3>
              <p className="rs-empty-desc">
                {handrails.length === 0
                  ? 'Add your first handrail to start tracking safety compliance.'
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          ) : filtered.map(handrail => (
            <div key={handrail.id} className="rs-mobile-card">
              <div className="rs-mobile-card-header">
                <span className="rs-mobile-card-title">{handrail.name}</span>
                <span className={getBadgeClass(handrail.status)}>{handrail.status || 'Unknown'}</span>
              </div>
              <div className="rs-mobile-card-meta">
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Location</span>
                  <span className="rs-mobile-card-value">{handrail.location}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Type</span>
                  <span className="rs-mobile-card-value">{handrail.type}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Material</span>
                  <span className="rs-mobile-card-value">{handrail.material}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Length</span>
                  <span className="rs-mobile-card-value">{handrail.length}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Last Test</span>
                  <span className="rs-mobile-card-value">{formatDate(handrail.lastTestDate)}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Next Test</span>
                  <span className="rs-mobile-card-value">{formatDate(handrail.nextTestDate)}</span>
                </div>
              </div>
              <div className="rs-mobile-card-actions">
                <button className="rs-btn rs-btn-secondary" onClick={() => handleViewTestData(handrail)}>
                  <FontAwesomeIcon icon={faEye} /> View
                </button>
                <button className="rs-btn rs-btn-secondary" onClick={() => setDetailAsset(handrail)}>
                  <FontAwesomeIcon icon={faClockRotateLeft} /> Test History
                </button>
                {isSuperAdmin() && (<>
                <button className="rs-btn rs-btn-secondary" onClick={() => { setEditingHandrail(handrail); setShowEditModal(true); }}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="rs-btn rs-btn-danger" onClick={() => handleDeleteHandrail(handrail.id)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
                </>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="rs-modal-overlay" role="dialog" aria-modal="true" aria-label="Add new handrail">
          <div className="rs-modal">
            <div className="rs-modal-header">
              <h3>Add New Handrail</h3>
              <button className="rs-modal-close" onClick={() => setShowCreateModal(false)} aria-label="Close modal">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); await handleCreateHandrail(); }}>
              <div className="rs-modal-body">
                {renderFormFields(newHandrail, setNewHandrail)}
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="rs-btn rs-btn-primary">Create Handrail</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingHandrail && (
        <div className="rs-modal-overlay" role="dialog" aria-modal="true" aria-label="Edit handrail">
          <div className="rs-modal">
            <div className="rs-modal-header">
              <h3>Edit Handrail</h3>
              <button className="rs-modal-close" onClick={() => { setShowEditModal(false); setEditingHandrail(null); }} aria-label="Close modal">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); await handleEditHandrail(); }}>
              <div className="rs-modal-body">
                {renderFormFields(editingHandrail, setEditingHandrail)}
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => { setShowEditModal(false); setEditingHandrail(null); }}>Cancel</button>
                <button type="submit" className="rs-btn rs-btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AssetDetailModal
        isOpen={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        asset={detailAsset ? { ...detailAsset, name: detailAsset.name || detailAsset.handrail_name, location: detailAsset.location } : null}
        assetType="handrail"
        propertyId={propertyId}
        assetMeta={detailAsset ? [
          { label: 'Type', value: detailAsset.type },
          { label: 'Length', value: detailAsset.length },
          { label: 'Height', value: detailAsset.height },
          { label: 'Material', value: detailAsset.material }
        ] : []}
      />
    </div>
  );
}
